import React, {useEffect, useMemo} from "react";
import {Modal, Button, Form} from "react-bootstrap";
import {useTranslation} from "react-i18next";
import Select, { components }  from "react-select";
import {usersList} from "../../../data";


const CustomOption = (props:any) => {
    const { data, innerRef, innerProps } = props;
    const { user } = data;
    return (
        <div ref={innerRef} {...innerProps} style={{ display: "flex", alignItems: "center", padding: 8 }}>
            <img
                src={user.memberImage}
                alt={user.memberName}
                style={{ width: 30, height: 30, borderRadius: "50%", marginRight: 10 }}
            />
            <div>{user.memberName}</div>
        </div>
    );
};

const CustomSingleValue = ({ data }:any) => {
    const { user } = data;
    return (
        <div style={{ display: "flex", alignItems: "center" }}>
            <img
                src={user.memberImage}
                alt={user.memberName}
                style={{ width: 24, height: 24, borderRadius: "50%", marginRight: 8 }}
            />
            {user.memberName}
        </div>
    );
};
const CustomMultiValueRemove = (props:any) => {
    if (props.data.user.id === 0) {
        return null;
    }
    return <components.MultiValueRemove {...props} />;
};

export const EditStore =  ({ show, store, onClose, onSave }: any) => {
    const {t} = useTranslation();
    const [formState, setFormState] = React.useState(store || {});
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);

    const options = useMemo(() => {
        return usersList.map((user) => ({
            value: user.id,
            label: user.memberName,
            user,
        }));
    }, []);

    const [selectedUsers, setSelectedUsers] = React.useState<any>([]);

    // Reset form state when store changes
    useEffect(() => {
        if (store) {
            setFormState(store);
            setError(null);
        } else {
            setFormState({});
        }
    }, [store]);

    useEffect(() => {
        if (store && store.users) {
            const selected = store.users
                .map((id: number) => options.find((o) => o.user.id === id))
                .filter(Boolean);
            setSelectedUsers(selected);
        } else {
            setSelectedUsers([]);
        }
    }, [store, options]);


    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormState({ ...formState, [name]: value });
    };

    const handleSubmit = async () => {
        // Validation
        if (!formState.name || !formState.name.trim()) {
            setError(t('Validation.field_required'));
            return;
        }

        setError(null);
        setIsSubmitting(true);

        try {
            const updatedStore = {
                ...formState,
                id: store?.id, // Ensure ID is included
                users: selectedUsers.map((u: any) => u.user.id),
            };
            await onSave(updatedStore);
        } catch (error: any) {
            setError(error?.message || t('Validation.validation_failed'));
        } finally {
            setIsSubmitting(false);
        }
    };


    return (
        <Modal show={show} onHide={onClose} backdrop="static" centered size="lg">
            <Modal.Header closeButton>
                <Modal.Title>{t('StoreList.edit_store')}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {error && (
                    <div className="alert alert-danger" role="alert">
                        {error}
                    </div>
                )}
                <Form>
                    <Form.Group className="mb-3">
                        <Form.Label>{t('StoreList.store_name')}</Form.Label>
                        <Form.Control
                            type="text"
                            name="name"
                            value={formState.name || ""}
                            onChange={handleChange}
                        />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>{t('StoreList.city')}</Form.Label>
                        <Form.Control
                            type="text"
                            name="city"
                            value={formState.city ? formState.city[0].toUpperCase() + formState.city.slice(1) : ""}
                            onChange={handleChange}
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>{t('StoreList.province')}</Form.Label>
                        <Form.Control
                            type="text"
                            name="province"
                            value={formState.province ? formState.province[0].toUpperCase() + formState.province.slice(1) : ""}
                            onChange={handleChange}
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>{t('StoreList.address')}</Form.Label>
                        <Form.Control
                            type="text"
                            name="address"
                            value={formState.address || ""}
                            onChange={handleChange}
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>{t('StoreList.phone')}</Form.Label>
                        <Form.Control
                            type="text"
                            name="phone"
                            value={formState.phone || ""}
                            onChange={handleChange}
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label htmlFor="choices-multiple-remove-button" className="text-muted">{t('StoreList.user_list')}</Form.Label>
                        <Select
                            options={options}
                            value={selectedUsers}
                            isMulti
                            getOptionLabel={(e:any) => e.label}
                            components={{
                                Option: CustomOption,
                                SingleValue: CustomSingleValue,
                                MultiValueRemove: CustomMultiValueRemove
                            }}
                            onChange={(selectedOptions: any[]) => {
                                const selectedValues = (selectedOptions || []).map((opt) => opt.value);
                                const filteredOptions = options.filter((opt) =>
                                    selectedValues.includes(opt.value)
                                );
                                const adminOption = options.find((o) => o.user.id === 0);
                                const final = adminOption
                                    ? [adminOption, ...filteredOptions.filter((o) => o.user.id !== 0)]
                                    : filteredOptions;
                                setSelectedUsers(final);
                            }}

                            placeholder={t('StoreList.select_user')}
                        />
                    </Form.Group>

                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="dark" onClick={onClose} disabled={isSubmitting}>{t('RuleList.cancel')}</Button>
                <Button variant="primary" onClick={handleSubmit} disabled={isSubmitting}>
                    {isSubmitting ? t('RuleList.saving') || 'Saving...' : t('RuleList.save')}
                </Button>
            </Modal.Footer>
        </Modal>
    );


}